import {
  Avatar,
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useIntl } from 'react-intl'
import { Title, Text } from '../../../components/typography'
import { colorPalette } from '../../../style/colorPalette'
import { ProjectApprovalBreakdown } from '../taskActualWorkApprovalStatus'

const AVATAR_SIZE = 20

type Props = {
  anchorEl: HTMLElement | null
  breakdown: ProjectApprovalBreakdown[]
  loading: boolean
  onClose: () => void
}

const formatHour = (value: number): string => {
  return value.toFixed(2)
}

export const ProjectBreakdownPopover = ({
  anchorEl,
  breakdown,
  loading,
  onClose,
}: Props) => {
  const intl = useIntl()
  const isOpen = Boolean(anchorEl)

  if (!isOpen) return null

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 2000 }}
      >
        <StyledPaper>
          <Header>
            <Title variant="s">
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.projectBreakdown.title',
              })}
            </Title>
          </Header>
          {loading ? (
            <LoadingArea>
              <CircularProgress size={24} />
            </LoadingArea>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <Th align="left">
                    <Text variant="2sBold" color={colorPalette.monotone[6]}>
                      {intl.formatMessage({
                        id: 'taskActualWorkApprovalStatus.projectBreakdown.project',
                      })}
                    </Text>
                  </Th>
                  <Th align="left">
                    <Text variant="2sBold" color={colorPalette.monotone[6]}>
                      {intl.formatMessage({
                        id: 'taskActualWorkApprovalStatus.projectBreakdown.projectManager',
                      })}
                    </Text>
                  </Th>
                  <Th align="right">
                    <Text variant="2sBold" color={colorPalette.monotone[6]}>
                      {intl.formatMessage({
                        id: 'taskActualWorkApprovalStatus.projectBreakdown.unRequested',
                      })}
                    </Text>
                  </Th>
                  <Th align="right">
                    <Text variant="2sBold" color={colorPalette.monotone[6]}>
                      {intl.formatMessage({
                        id: 'taskActualWorkApprovalStatus.projectBreakdown.unApproved',
                      })}
                    </Text>
                  </Th>
                </tr>
              </TableHead>
              <tbody>
                {breakdown.map(row => (
                  <tr key={row.projectUuid}>
                    <Td>
                      <CellContent>
                        <Avatar
                          src={row.projectIconUrl ?? undefined}
                          variant="rounded"
                          sx={{
                            width: AVATAR_SIZE,
                            height: AVATAR_SIZE,
                            backgroundColor: colorPalette.monotone[2],
                            flexShrink: 0,
                          }}
                        />
                        <ProjectNameBlock>
                          {row.projectCode && (
                            <Text variant="2s" color={colorPalette.monotone[4]}>
                              {row.projectCode}
                            </Text>
                          )}
                          <Text variant="2s">{row.projectDisplayName}</Text>
                        </ProjectNameBlock>
                      </CellContent>
                    </Td>
                    <Td>
                      {row.projectManagerName ? (
                        <CellContent>
                          <Avatar
                            src={row.projectManagerIconUrl ?? undefined}
                            variant="circular"
                            sx={{
                              width: AVATAR_SIZE,
                              height: AVATAR_SIZE,
                              backgroundColor: colorPalette.monotone[2],
                              flexShrink: 0,
                            }}
                          />
                          <Text variant="2s">{row.projectManagerName}</Text>
                        </CellContent>
                      ) : null}
                    </Td>
                    <NumTd>
                      <Text variant="2s">
                        {formatHour(row.unRequestedHour)}
                      </Text>
                    </NumTd>
                    <NumTd>
                      <Text variant="2s">{formatHour(row.unApprovedHour)}</Text>
                    </NumTd>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </StyledPaper>
      </Popper>
    </ClickAwayListener>
  )
}

const StyledPaper = styled(Paper)({
  minWidth: 480,
  maxWidth: 700,
  maxHeight: 400,
  overflow: 'auto',
  padding: '12px 16px',
})

const Header = styled('div')({
  marginBottom: 8,
})

const LoadingArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 0',
})

const Table = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
})

const TableHead = styled('thead')({
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const Th = styled('th')<{ align?: string }>(({ align = 'left' }) => ({
  padding: '4px 8px',
  textAlign: align as 'left' | 'right' | 'center',
  whiteSpace: 'nowrap',
}))

const Td = styled('td')({
  padding: '4px 8px',
  verticalAlign: 'middle',
})

const NumTd = styled('td')({
  padding: '4px 8px',
  verticalAlign: 'middle',
  textAlign: 'right',
  whiteSpace: 'nowrap',
})

const CellContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  overflow: 'hidden',
})

const ProjectNameBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflow: 'hidden',
})
